/**
 * Tipos TypeScript para sistema de credenciais seguras
 * Utilizado para armazenar API keys, tokens e outras credenciais por tenant
 * 
 * RELEASE: 1.0.0 - 2024-12-24
 */

/**
 * Tipos de credenciais suportados pelo sistema
 */
export type CredentialType = 'api_key' | 'basic_auth' | 'bearer_token' | 'custom';

/**
 * Locais onde a credencial pode ser injetada na requisição
 */
export type KeyLocation = 'header' | 'body' | 'query';

/**
 * Credencial do tipo API Key
 * Usado para APIs que requerem uma chave simples
 */
export interface ApiKeyCredentialData {
  key_name: string;       // Nome do campo (ex: "api_key", "X-Api-Key")
  key_value: string;      // Valor da chave
  key_location: KeyLocation; // Onde inserir na requisição
}

/**
 * Credencial do tipo Basic Auth
 * Usado para APIs com autenticação HTTP básica
 */
export interface BasicAuthCredentialData {
  username: string;
  password: string;
}

/**
 * Credencial do tipo Bearer Token
 * Usado para APIs com OAuth/JWT
 */
export interface BearerTokenCredentialData {
  token: string;
}

/**
 * Credencial customizada
 * Campos livres definidos pelo usuário
 */
export interface CustomCredentialData {
  fields: Record<string, string>;
  /** Destino de injeção por campo (header, body, query). Retrocompatível: default 'header' */
  field_locations?: Record<string, KeyLocation>;
}

/**
 * União de todos os tipos de dados de credenciais
 */
export type CredentialData = 
  | ApiKeyCredentialData 
  | BasicAuthCredentialData 
  | BearerTokenCredentialData 
  | CustomCredentialData;

/**
 * Registro de credencial armazenado no banco
 */
export interface TenantCredential {
  id: string;
  tenant_id: string;
  name: string;
  credential_type: CredentialType;
  credentials: CredentialData;
  description: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/**
 * Input para criar nova credencial
 */
export interface CreateCredentialInput {
  name: string;
  credential_type: CredentialType;
  credentials: CredentialData;
  description?: string;
}

/**
 * Input para atualizar credencial existente
 */
export interface UpdateCredentialInput {
  name?: string;
  credential_type?: CredentialType;
  credentials?: CredentialData;
  description?: string;
  is_active?: boolean;
}

/**
 * Credencial segura retornada pela RPC list_tenant_credentials_safe
 * NÃO contém o campo credentials (tokens/API keys)
 */
export type SafeTenantCredential = Omit<TenantCredential, 'credentials'>;

/**
 * Opções de tipo de credencial para UI
 */
export const CREDENTIAL_TYPE_OPTIONS = [
  { 
    value: 'api_key' as CredentialType, 
    label: 'API Key',
    description: 'Chave de API simples (ex: api_key, X-Api-Key)'
  },
  { 
    value: 'basic_auth' as CredentialType, 
    label: 'Basic Auth',
    description: 'Usuário e senha (HTTP Basic Authentication)'
  },
  { 
    value: 'bearer_token' as CredentialType, 
    label: 'Bearer Token',
    description: 'Token OAuth/JWT (Authorization: Bearer)'
  },
  { 
    value: 'custom' as CredentialType, 
    label: 'Personalizado',
    description: 'Campos customizados definidos por você'
  },
] as const;

/**
 * Opções de localização da chave para UI
 */
export const KEY_LOCATION_OPTIONS = [
  { 
    value: 'body' as KeyLocation, 
    label: 'Body da requisição',
    description: 'Inserir no JSON do corpo (mais comum)'
  },
  { 
    value: 'header' as KeyLocation, 
    label: 'Header HTTP',
    description: 'Inserir como cabeçalho HTTP'
  },
  { 
    value: 'query' as KeyLocation, 
    label: 'Query Parameter',
    description: 'Inserir como parâmetro na URL'
  },
] as const;
